import { useCallback, useEffect, useState } from 'react'
import { getRuntime } from './_mode'
import type { WorkspaceSummary } from './types'
import { api, ApiError, isAuthError } from '../api/client'
import { captureHookFailure, recordHookOutcome } from '../observability/sentry'

export interface UseWorkspacesFilters {
  scope?: 'personal' | 'project'
  project_id?: string
  current_mode?: string
  pinned?: boolean
  limit?: number
}

function normalizeWorkspace(row: {
  id: string
  name: string
  slug: string
  scope: 'personal' | 'project'
  project_id: string | null
  current_mode: string
  default_mode: string
  status: string
  prompt: string | null
  metadata_json: Record<string, unknown>
  updated_at: string
}): WorkspaceSummary {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    scope: row.scope,
    project_id: row.project_id,
    current_mode: row.current_mode,
    default_mode: row.default_mode,
    status: row.status,
    prompt: row.prompt,
    pinned: Boolean(row.metadata_json?.pinned),
    updated_at: row.updated_at,
  }
}

export function useWorkspaces(filters: UseWorkspacesFilters = {}) {
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setAuthRequired(false)

    const { live } = getRuntime()
    if (!live) {
      setWorkspaces([])
      setLoading(false)
      recordHookOutcome('useWorkspaces', 'demo_fallback', {
        route: '/api/workspace/list',
        rows: 0,
      })
      return
    }

    const startedAt = Date.now()
    try {
      const result = await api.workspace.list(filters)
      const rows = (result.workspaces || []).map(normalizeWorkspace)
      setWorkspaces(rows)
      recordHookOutcome('useWorkspaces', 'live_success', {
        route: '/api/workspace/list',
        rows: rows.length,
        durationMs: Date.now() - startedAt,
      })
    } catch (err) {
      const apiErr = err as ApiError
      setWorkspaces([])
      if (isAuthError(err)) {
        setError(null)
        setAuthRequired(true)
      } else {
        setError(apiErr?.message || 'workspace_list_failed')
      }
      captureHookFailure('useWorkspaces', 'live_error', err, {
        route: '/api/workspace/list',
        code: apiErr?.code,
        status: apiErr?.status,
        durationMs: Date.now() - startedAt,
      })
    } finally {
      setLoading(false)
    }
  }, [filters.current_mode, filters.limit, filters.pinned, filters.project_id, filters.scope])

  useEffect(() => {
    void load()
  }, [load])

  const create = useCallback(
    async (payload: {
      name: string
      scope: 'personal' | 'project'
      projectId?: string
      mode?: string
    }) => {
      const { live } = getRuntime()
      if (!live) {
        // Honest demo branch — refuse to fake a create.
        throw new ApiError(503, 'demo_mode', 'workspace create is disabled in demo mode')
      }
      const result = await api.workspace.create(payload)
      await load()
      return normalizeWorkspace(result.workspace as any)
    },
    [load],
  )

  return {
    workspaces,
    loading,
    error,
    authRequired,
    live: getRuntime().live,
    refresh: load,
    create,
  }
}
